// /src/markdown/parseInlineAttrs.ts
// Robust inline attribute parser for tokens like:
//   image: ror.png { position: top-left, anim: slide-right, delay: 0.8, dur: 3000ms, class: "object-contain mx-auto" }

export function parseInlineAttrs(spec: string): Record<string, any> {
  const attrs: Record<string, any> = {};
  const cleaned = String(spec || "")
    .replace(/^\s*(?:image:|video:|audio:)\s*/i, "")
    .trim();

  const pairs = splitOnCommasOutsideQuotes(cleaned)
    .map((p) => p.trim())
    .filter(Boolean);

  for (const pair of pairs) {
    const idx = findFirstColonOutsideQuotes(pair);
    if (idx === -1) {
      const k = pair.trim();
      // Ignore stray HTML-like fragments (e.g., "<a href='...'>")
      if (/^<\/?\w[\s\S]*>$/.test(k)) continue;
      if (k) attrs[k] = true;
      continue;
    }
    const key = pair.slice(0, idx).trim();
    const rawVal = pair.slice(idx + 1).trim();
    if (!key) continue;
    attrs[key] = coerce(rawVal);
  }

  normalizeLinkAttrs(attrs);
  return attrs;
}

/** Split on commas OR newlines NOT inside single/double quotes (and ignore commas inside (...) ) */
function splitOnCommasOutsideQuotes(input: string): string[] {
  const out: string[] = [];
  let buf = "";
  let inSingle = false;
  let inDouble = false;
  let parenDepth = 0;
  let escaped = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i]!;

    if (!escaped) {
      // handle escape inside quotes
      if ((inSingle || inDouble) && ch === "\\") { escaped = true; buf += ch; continue; }
      if (ch === "'" && !inDouble) { inSingle = !inSingle; buf += ch; continue; }
      if (ch === '"' && !inSingle) { inDouble = !inDouble; buf += ch; continue; }

      if (!inSingle && !inDouble) {
        if (ch === "(") parenDepth++;
        else if (ch === ")" && parenDepth > 0) parenDepth--;
        // delimiters: comma or newline when not inside quotes/paren
        if ((ch === "," || ch === "\n") && parenDepth === 0) {
          out.push(buf);
          buf = "";
          continue;
        }
      }
    } else {
      // consume escaped next character in quotes
      escaped = false;
    }

    buf += ch;
  }

  if (buf) out.push(buf);
  return out;
}

/** Find the first ':' not inside quotes (escape-aware) */
function findFirstColonOutsideQuotes(input: string): number {
  let inSingle = false;
  let inDouble = false;
  let escaped = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i]!;

    if (!escaped) {
      if ((inSingle || inDouble) && ch === "\\") { escaped = true; continue; }
      if (ch === "'" && !inDouble) { inSingle = !inSingle; continue; }
      if (ch === '"' && !inSingle) { inDouble = !inDouble; continue; }
      if (ch === ":" && !inSingle && !inDouble) return i;
    } else {
      escaped = false;
    }
  }
  return -1;
}

/** Remove surrounding quotes if present (unescape-aware) */
function stripQuotes(v: string): string {
  const quoted =
    (v.startsWith('"') && v.endsWith('"')) ||
    (v.startsWith("'") && v.endsWith("'"));
  if (!quoted) return v;
  const inner = v.slice(1, -1);
  // Unescape minimal set used in authoring: \" \' and \\
  return inner.replace(/\\(["'\\])/g, "$1");
}

/** Coerce booleans/numbers; keep urls & duration tokens intact */
function coerce(val: string): any {
  const v0 = val.trim();
  if (!v0) return "";

  // Keep quoted strings verbatim (minus outer quotes)
  if ((v0.startsWith('"') && v0.endsWith('"')) || (v0.startsWith("'") && v0.endsWith("'"))) {
    return stripQuotes(v0);
  }

  // Keep URL-like tokens intact
  if (/^(https?:\/\/|\/\/|mailto:|tel:|#)/i.test(v0)) return v0;

  // Booleans / null (case-insensitive)
  const lower = v0.toLowerCase();
  if (lower === "true") return true;
  if (lower === "false") return false;
  if (lower === "null") return null;

  // Duration tokens: keep units by default (e.g., "3000ms", "0.8s")
  // If you prefer seconds as number, flip the return to `unit === 'ms' ? n/1000 : n`
  const t = v0.match(/^(-?\d+(?:\.\d+)?)\s*(ms|s)$/i);
  if (t) return v0; // keep as provided, e.g., "4000ms"

  // Hex colors or CSS variables pass-through
  if (v0.startsWith("#") || /^var\(/i.test(v0)) return v0;

  // Plain number
  const num = Number(v0);
  if (!Number.isNaN(num) && Number.isFinite(num)) return num;

  // Fallback: raw string
  return v0;
}

/** Normalize link/href values: strip accidental <a ...> wrappers and stray tags */
function normalizeLinkAttrs(a: Record<string, any>): void {
  for (const key of ["link", "href"]) {
    if (typeof a[key] !== "string") continue;
    let v = a[key].trim();

    // If value is an anchor tag, extract the href
    const m = v.match(/<a\s+[^>]*href=['"]([^'"]+)['"][^>]*>/i);
    if (m && m[1]) {
      a[key] = m[1];
      continue;
    }

    // Strip any remaining HTML tags defensively
    if (v.startsWith("<") && v.endsWith(">")) {
      a[key] = v.replace(/<[^>]+>/g, "").trim();
    }
  }
}